/**
 * Jobs (JOB-LIFECYCLE slice 1) — a Job is the folder for one sold piece of work
 * (design: research/job-lifecycle-design.md). It holds Visits (pegasus_dispatch
 * rows linked by job_id) and, later, equipment/permit/invoice. "Sell an install"
 * creates the job + an (unscheduled) install visit. ESC stays read-only (DEC-22);
 * everything here is Pegasus-native.
 */
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { checkSubResend } from "./resendPrompt";

export interface Job {
  id: string;
  custno: string;
  locno: string | null;
  title: string | null;
  job_type: string;
  status: string;
  sale_tech: string | null;
  sell_price: number | null;
  source_dispatch: string | null;
  sold_estimate_ref: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
  closed_at: string | null;
  /** Customer-facing message printed on the install invoice (office/installer authored). */
  invoice_message: string | null;
  // Pegasus-native site (new-construction lots etc.) + NC supervisor
  site_address?: string | null;
  site_city?: string | null;
  /** Clean city token for the service address (site_city for NC, else mirror_location.city) — the permit Jurisdiction default. Resolved in resolveNames(). */
  service_city?: string | null;
  /** Manual overrides for the derived lifecycle benchmarks, e.g. { permit: true }. */
  manual_checks?: Record<string, boolean> | null;
  /** INSTALL-SPEC-01 — the cover-sheet install details (PVC reuse/new, lineset,
   *  pad, condenser location, reconnects, key arrangements, comments). */
  install_spec?: Record<string, string> | null;
  supervisor?: string | null;
  property_id?: string | null;
  /** PERMIT-01: who pulls the permit — null = not decided (installs read it as "sedgwick"). */
  permit_by?: PermitBy | null;
  /** NC-DRAW-02: this house's own draw split ([{label,pct}]); null = the builder's default. */
  draw_plan?: unknown;
  /** NC-DRAW-02: the builder's PO for this house — prints on every draw. */
  po_number?: string | null;
  /* FIELD-PAY-01: how they're paying (from the sale; the office can change it). */
  pay_plan?: "full" | "financing" | "bill_after" | null;
  fin_plan_id?: string | null;
  fin_status?: "applied" | "approved" | "funded" | "declined" | null;
  fin_approval_no?: string | null;
  fin_amount?: number | null;
  fin_updated_at?: string | null;
  fin_updated_by?: string | null;
  /* INSTALLER-APP-01b: the customer's walkthrough sign-off + Install Complete. */
  signoff_name?: string | null;
  signoff_signature?: string | null;
  signoff_at?: string | null;
  signoff_by?: string | null;
  crew_finished_at?: string | null;
  /** CLOSE-INSTALL-DATE: the install date the office set at close (null = the crew's finish day). */
  install_date?: string | null;
  crew_finished_by?: string | null;
  crew_finished_note?: string | null;
  // resolved for display (not stored on the job)
  customer_name?: string | null;
  address?: string | null;
  // costing roll-up from v_job_costing (attached in useJobs)
  materials?: number; equipment?: number; labor?: number; permits?: number;
  /** REFRIG-01: the crew's field materials + the refrigerant charged (same math as the job page). */
  field_materials?: number; refrigerant_cost?: number;
  quoted?: number; actual?: number; variance?: number;
  /** REBATE-TEST-01: the office's switch for the Rebate Test Form (null = automatic). */
  rebate_test?: "needed" | "not_needed" | null;
  /** TEST-FLAG-01: a test job — it and its visits never message anyone; only a test job can be deleted. */
  is_test?: boolean;
  /* JOB-SOLD-01: the sale handoff — what the phone sent, the load calc, the Equipment Check. */
  sale_packet?: import("./saleHandoff").SalePacket | null;
  load_calc?: import("./saleHandoff").LoadCalcRecord | null;
  equip_confirmed_at?: string | null;
  equip_confirmed_by?: string | null;
  equip_confirmed_sig?: string | null;
  equip_confirm_note?: string | null;
  equip_check?: import("./saleHandoff").EquipCheckRecord | null;
}

export interface JobVisit {
  id: string;
  visit_role: string | null;
  summary: string | null;
  customer_name: string | null;
  address: string | null;
  scheduled_date: string | null;
  scheduled_window: string | null;
  assigned_empno: string | null;
  status: string | null;
  notes: string | null;
  source: string | null;
}

/** Pipeline stages (the office install board order). */
export const JOB_STAGES = ["sold", "permit_pending", "scheduled", "in_progress", "ready_to_close", "closed"] as const;
export const STAGE_LABEL: Record<string, string> = {
  sold: "Sold", permit_pending: "Permit pending", scheduled: "Scheduled",
  in_progress: "In progress", ready_to_close: "Ready to close", closed: "Closed", cancelled: "Cancelled",
};

interface LocRow { custno: string; locno: string | null; locname: string | null; add1: string | null; city: string | null }

/** Clean an ESC fullname: strip trailing custno, "Last,First" → "First Last". */
function cleanCustName(fullname: string | null): string | null {
  if (!fullname) return null;
  let s = fullname.replace(/\s+\d{4,}\s*$/, "").trim();
  const m = s.match(/^([^,]+),\s*(.+)$/);
  if (m) s = `${m[2]} ${m[1]}`.trim();
  return s || null;
}

/** Customer name + service address for jobs (the ESC location, or the NC site). */
export async function resolveNames(jobs: Job[]): Promise<Job[]> {
  const keys = [...new Set(jobs.map((j) => j.custno).filter(Boolean))];
  if (keys.length === 0) return jobs;
  // Customer NAME (builder) comes from mirror_customer.fullname; the home/property
  // address comes from the location — distinct for new construction (one builder,
  // many homes).
  const [{ data: locs }, { data: custs }] = await Promise.all([
    supabase.from("mirror_location").select("custno,locno,locname,add1,city").in("custno", keys),
    supabase.from("mirror_customer").select("custno,fullname").in("custno", keys),
  ]);
  const byKey = new Map<string, LocRow>(); const byCust = new Map<string, LocRow>();
  for (const l of (locs ?? []) as LocRow[]) { byKey.set(`${l.custno}|${l.locno}`, l); if (!byCust.has(l.custno)) byCust.set(l.custno, l); }
  const nameByCust = new Map((custs ?? []).map((c: { custno: string; fullname: string | null }) => [c.custno, cleanCustName(c.fullname)]));
  return jobs.map((j) => {
    const loc = byKey.get(`${j.custno}|${j.locno}`) ?? byCust.get(j.custno);
    // A Pegasus-native site address (new-construction lot) wins over the ESC location.
    const siteAddr = j.site_address ? `${j.site_address}${j.site_city ? ", " + j.site_city : ""}` : null;
    return {
      ...j,
      customer_name: nameByCust.get(j.custno) ?? loc?.locname ?? j.custno,
      address: siteAddr ?? (loc ? `${loc.add1 ?? ""}${loc.city ? ", " + loc.city : ""}` : null),
      service_city: j.site_city ?? loc?.city ?? null,
    };
  });
}

export function useJobs() {
  return useQuery({
    queryKey: ["jobs"],
    staleTime: 15 * 1000,
    queryFn: async (): Promise<Job[]> => {
      const { data, error } = await supabase.from("pegasus_job").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      const jobs = await resolveNames((data ?? []) as Job[]);
      if (jobs.length === 0) return [];
      const { data: cost } = await supabase.from("v_job_costing").select("*").in("job_id", jobs.map((j) => j.id));
      const cmap = new Map((cost ?? []).map((c: { job_id: string } & Record<string, number>) => [c.job_id, c]));
      return jobs.map((j) => {
        const c = cmap.get(j.id);
        const materials = Number(c?.materials ?? 0), equipment = Number(c?.equipment ?? 0);
        const labor = Number(c?.labor ?? 0), permits = Number(c?.permits ?? 0);
        const field_materials = Number(c?.field_materials ?? 0), refrigerant_cost = Number(c?.refrigerant ?? 0);
        const quoted = Number(c?.quoted ?? j.sell_price ?? 0);
        const actual = materials + field_materials + equipment + labor + permits + refrigerant_cost;
        return { ...j, materials, equipment, labor, permits, field_materials, refrigerant_cost, quoted, actual, variance: quoted - actual };
      });
    },
  });
}

/** WORK-QUEUES-01 — which open jobs need which person's action, DERIVED.
 *  needs_schedule: an install visit still pending_schedule (install coordinator)
 *  needs_permit:   we pull the permit (or nobody decided yet) and none of ours
 *                  is on file — Requested doesn't count (the permit person)
 *  permit_requested: the crew or office asked for a permit that nobody has
 *                  pulled yet — the hottest part of the permit desk's list
 *  ready_to_close: serial logged + permit on file / not ours, still open
 *  Post-install joins this list when POST-INSTALL-01's stations land, so the
 *  queue reflects real pending sends — not a guess. */
export interface JobQueues {
  needsSchedule: Set<string>;
  needsPermit: Set<string>;
  permitRequested: Set<string>;
  readyToClose: Set<string>;
}
export function useJobQueues(jobs: Job[] | undefined) {
  const open = (jobs ?? []).filter((j) => j.status !== "closed" && j.status !== "cancelled");
  const ids = open.map((j) => j.id);
  const byId = new Map(open.map((j) => [j.id, j]));
  return useQuery({
    queryKey: ["job_queues", ids.join("|"), open.map((j) => j.permit_by ?? "").join("|")],
    enabled: ids.length > 0,
    staleTime: 30 * 1000,
    queryFn: async (): Promise<JobQueues> => {
      const [{ data: pend }, { data: perms }, { data: serials }, { data: waived }] = await Promise.all([
        supabase.from("pegasus_dispatch").select("job_id").in("job_id", ids).eq("status", "pending_schedule").is("deleted_at", null),
        supabase.from("pegasus_permit").select("job_id, status, by_builder").in("job_id", ids),
        supabase.from("pegasus_job_equipment").select("job_id, serial").in("job_id", ids).not("serial", "is", null),
        // POST-INSTALL-02: "No Permit Needed" takes a job OUT of the permit
        // queue — a blank permits tab no longer nags forever (Seth 2026-08-21).
        supabase.from("pegasus_post_install").select("job_id").in("job_id", ids).eq("permit_required", false),
      ]);
      const pendSet = new Set(((pend ?? []) as { job_id: string }[]).map((r) => r.job_id));
      const permRows = (perms ?? []) as { job_id: string; status: string; by_builder: boolean }[];
      const onFileSet = new Set(permRows.filter(permitIsOnFile).map((r) => r.job_id));
      const requestedSet = new Set(permRows.filter((r) => r.status === "requested" && !r.by_builder).map((r) => r.job_id));
      const serialSet = new Set(((serials ?? []) as { job_id: string; serial: string | null }[]).filter((r) => (r.serial ?? "").trim()).map((r) => r.job_id));
      const waivedSet = new Set(((waived ?? []) as { job_id: string | null }[]).map((r) => r.job_id).filter(Boolean) as string[]);
      // PERMIT-01: builder-pulled / not-needed jobs never sit in the permit queue.
      const notOurs = (id: string) => {
        const by = effectivePermitBy(byId.get(id), waivedSet.has(id) ? { permit_required: false } : null);
        return by === "builder" || by === "none";
      };
      const permOk = (id: string) => onFileSet.has(id) || notOurs(id);
      return {
        needsSchedule: new Set(ids.filter((id) => pendSet.has(id))),
        needsPermit: new Set(ids.filter((id) => !permOk(id))),
        permitRequested: new Set(ids.filter((id) => requestedSet.has(id) && !permOk(id))),
        readyToClose: new Set(ids.filter((id) => serialSet.has(id) && permOk(id))),
      };
    },
  });
}

export function useJob(id: string | null) {
  return useQuery({
    queryKey: ["job", id],
    enabled: !!id,
    queryFn: async (): Promise<Job | null> => {
      const { data } = await supabase.from("pegasus_job").select("*").eq("id", id!).maybeSingle();
      if (!data) return null;
      return (await resolveNames([data as Job]))[0];
    },
  });
}

/** The accepted proposal this job came from (pegasus_quote.job_id link),
 *  with how the customer chose to pay (PLAN-SHOW-01 — the financing plan). */
export interface JobQuote {
  id: string;
  title: string | null;
  accepted_at: string | null;
  accepted_option_id: string | null;
  payment_preference: string | null;
  acceptance_json: { financing_plan_id?: string | null } | null;
}
export function useJobQuote(jobId: string | null) {
  return useQuery({
    queryKey: ["job_quote", jobId],
    enabled: !!jobId,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<JobQuote | null> => {
      const { data } = await supabase.from("pegasus_quote")
        .select("id, title, accepted_at, accepted_option_id, payment_preference, acceptance_json").eq("job_id", jobId!).maybeSingle();
      return (data as JobQuote | null) ?? null;
    },
  });
}

export function useJobVisits(jobId: string | null) {
  return useQuery({
    queryKey: ["job_visits", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobVisit[]> => {
      const { data } = await supabase.from("pegasus_dispatch")
        .select("id,visit_role,summary,customer_name,address,scheduled_date,scheduled_window,assigned_empno,status,notes,source")
        .eq("job_id", jobId!)
        .is("deleted_at", null)
        .order("scheduled_date", { ascending: true, nullsFirst: false });
      return (data ?? []) as JobVisit[];
    },
  });
}

/**
 * Assign an installer + date + arrival window to an install visit (the
 * pegasus_dispatch row). Sets assigned_empno / scheduled_date / scheduled_window
 * and flips status to "scheduled" so it lands on the board's Install lens;
 * clearing the date parks it back to pending_schedule. ESC untouched (DEC-22).
 */
export function useAssignJobVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      id: string;
      job_id: string;
      assigned_empno: string | null;
      scheduled_date: string | null;
      scheduled_window: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const scheduled = !!v.scheduled_date;
      const { error } = await supabase.from("pegasus_dispatch").update({
        assigned_empno: v.assigned_empno || null,
        scheduled_date: v.scheduled_date || null,
        scheduled_window: v.scheduled_window?.trim() || null,
        status: scheduled ? "scheduled" : "pending_schedule",
        scheduled_at: scheduled ? new Date().toISOString() : null,
        scheduled_by: u?.user?.email ?? null,
      }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["job_visits", v.job_id] });
      qc.invalidateQueries({ queryKey: ["job", v.job_id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["board"] });
      // SUB-AUTO-RESEND: the install moved (or lost its date) after the sub got it → ask.
      void checkSubResend({ jobId: v.job_id });
    },
  });
}

/**
 * Create an unscheduled install visit for a job that doesn't have one yet (e.g.
 * a job that came from an accepted proposal). Mirrors the sell-flow's dispatch
 * insert; the office then assigns installer + date + window via useAssignJobVisit.
 */
export function useCreateInstallVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: {
      job_id: string;
      custno: string | null;
      locno: string | null;
      customer_name: string | null;
      address: string | null;
      summary: string | null;
    }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_dispatch").insert({
        lane_key: "install", status: "pending_schedule", visit_role: "install", job_id: v.job_id,
        custno: v.custno, locno: v.locno, customer_name: v.customer_name, address: v.address,
        summary: v.summary || "System install", source: "from_dispatch", created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["job_visits", v.job_id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/**
 * SALE-FINALIZE-01 (Seth 2026-10-14: "customer accepts after the fact. Tech didn't
 * measure at the time … we need a dispatch on the board for the tech to go finalize
 * the sale and then launch into the builder"). A SITE-SURVEY visit on the board,
 * pinned to the selling tech: on the phone that stop opens the same Install Packet
 * an on-site sale uses (photos, site facts, installer guide → the Sold stage).
 * Industry name for this is a site survey / pre-install walkthrough; ServiceTitan
 * and Jobber both model it as a visit on the sold job, which is exactly this.
 */
export function useCreateFinalizeVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job: Pick<Job, "id" | "custno" | "locno" | "title" | "sell_price" | "sale_tech" | "site_address" | "site_city">; why?: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      let name: string | null = null, phone: string | null = null, add1 = v.job.site_address, city = v.job.site_city;
      if (v.job.custno) {
        const [{ data: cust }, { data: loc }] = await Promise.all([
          supabase.from("mirror_customer").select("fullname, phone1").eq("custno", v.job.custno).maybeSingle(),
          v.job.locno
            ? supabase.from("mirror_location").select("add1, city, phone1").eq("custno", v.job.custno).eq("locno", v.job.locno).maybeSingle()
            : Promise.resolve({ data: null }),
        ]);
        const c = cust as { fullname?: string | null; phone1?: string | null } | null;
        const l = loc as { add1?: string | null; city?: string | null; phone1?: string | null } | null;
        name = c?.fullname?.trim() || null;
        phone = l?.phone1 ?? c?.phone1 ?? null;
        add1 = add1 ?? l?.add1 ?? null;
        city = city ?? l?.city ?? null;
      }
      const price = v.job.sell_price != null ? Number(v.job.sell_price).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : null;
      const { error } = await supabase.from("pegasus_dispatch").insert({
        lane_key: "sale_visit",
        visit_role: "survey",
        status: "pending_schedule",
        job_id: v.job.id,
        custno: v.job.custno,
        locno: v.job.locno,
        customer_name: name ?? v.job.custno ?? "Customer",
        address: add1,
        city,
        phone,
        affinity_empno: v.job.sale_tech ?? null,
        summary: `Finalize the sale — site photos, measurements & install details${price ? ` (${price})` : ""}`,
        notes: `${v.why?.trim() || "The homeowner accepted after the visit, so the install packet is empty."} ` +
          `Capture the site photos, the facts and the installer guide on the phone (Install Packet) so the office can run the load calc and schedule the crew.` +
          (v.job.title ? ` Sold: ${v.job.title}.` : ""),
        quoted_amount: v.job.sell_price ?? null,
        est_install_minutes: 45,
        source: "job",
        created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["job_visits", v.job.id] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

/** Toggle a manual override on a derived lifecycle benchmark. Effective "done" =
 *  derived-from-work OR this manual flag, so the office can hand-tick a step. */
export function useToggleJobCheck() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; manual_checks: Record<string, boolean> }) => {
      const { error } = await supabase.from("pegasus_job").update({ manual_checks: v.manual_checks }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => {
      qc.invalidateQueries({ queryKey: ["job", v.id] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
    },
  });
}

export interface JobBenchmark { id: string; job_id: string; label: string; done: boolean; sort: number | null }

/** Custom, office-defined checklist items for a job (purely manual). */
export function useJobBenchmarks(jobId: string | null) {
  return useQuery({
    queryKey: ["job_benchmarks", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobBenchmark[]> => {
      const { data } = await supabase.from("pegasus_job_benchmark")
        .select("id, job_id, label, done, sort").eq("job_id", jobId!)
        .order("sort", { nullsFirst: false }).order("created_at").order("id");
      return (data ?? []) as JobBenchmark[];
    },
  });
}

export function useAddJobBenchmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; label: string }) => {
      const label = v.label.trim();
      if (!label) throw new Error("Name the item first");
      const { error } = await supabase.from("pegasus_job_benchmark").insert({ job_id: v.job_id, label });
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["job_benchmarks", v.job_id] }),
  });
}

export function useToggleJobBenchmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; job_id: string; done: boolean }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_job_benchmark")
        .update({ done: v.done, done_at: v.done ? new Date().toISOString() : null, done_by: v.done ? (u?.user?.email ?? null) : null })
        .eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["job_benchmarks", v.job_id] }),
  });
}

export function useDeleteJobBenchmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; job_id: string }) => {
      const { error } = await supabase.from("pegasus_job_benchmark").delete().eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["job_benchmarks", v.job_id] }),
  });
}

// ── Invoice line customization (invoice-only overlay; never touches job data) ──
export interface InvoiceLineEdit {
  id: string; job_id: string; line_key: string | null; is_custom: boolean;
  hidden: boolean; sort: number | null; label: string | null; amount: number | null;
}

export function useInvoiceLineEdits(jobId: string | null) {
  return useQuery({
    queryKey: ["invoice_line_edits", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<InvoiceLineEdit[]> => {
      const { data } = await supabase.from("pegasus_invoice_line_edit")
        .select("id, job_id, line_key, is_custom, hidden, sort, label, amount")
        .eq("job_id", jobId!).order("created_at").order("id");
      return (data ?? []) as InvoiceLineEdit[];
    },
  });
}

/** Toggle a derived line's visibility on the invoice (upsert by job_id+line_key). */
export function useSetInvoiceLineHidden() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; line_key: string; hidden: boolean; label?: string | null }) => {
      const { data: existing } = await supabase.from("pegasus_invoice_line_edit")
        .select("id").eq("job_id", v.job_id).eq("line_key", v.line_key).maybeSingle();
      if (existing) {
        const { error } = await supabase.from("pegasus_invoice_line_edit")
          .update({ hidden: v.hidden, label: v.label, updated_at: new Date().toISOString() }).eq("id", (existing as { id: string }).id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("pegasus_invoice_line_edit")
          .insert({ job_id: v.job_id, line_key: v.line_key, hidden: v.hidden, label: v.label ?? null });
        if (error) throw error;
      }
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["invoice_line_edits", v.job_id] }),
  });
}

export function useAddCustomInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; label: string; amount: number | null }) => {
      const label = v.label.trim();
      if (!label) throw new Error("Name the line first");
      const { error } = await supabase.from("pegasus_invoice_line_edit")
        .insert({ job_id: v.job_id, is_custom: true, label, amount: v.amount });
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["invoice_line_edits", v.job_id] }),
  });
}

export function useDeleteInvoiceLineEdit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; job_id: string }) => {
      const { error } = await supabase.from("pegasus_invoice_line_edit").delete().eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["invoice_line_edits", v.job_id] }),
  });
}

/** Best-effort manufacturer from the model prefix (Lennox / Braeburn shop). */
export function brandOf(model: string | null): string {
  if (!model) return "—";
  const m = model.toUpperCase();
  if (/^(BR|BB|BRA)/.test(m)) return "Braeburn";
  if (/^(ML|CB|CK|KC|EL|SL|XC|XP|13|14|16)/.test(m)) return "Lennox";
  return "—";
}
/** Title-case a stored manufacturer ("BROAN" → "Broan", "AIR EASE" → "Air Ease"). */
export function tcase(s: string | null): string { return (s ?? "").toLowerCase().replace(/\b\w/g, (c) => c.toUpperCase()); }
/**
 * Manufacturer for display. The STORED manufacturer always wins (title-cased); only
 * when it's missing do we guess from the model prefix. ONE source of truth so the
 * on-screen preview and the printed invoice can never disagree — they did, which is
 * why an Aprilaire humidifier ("600A", no Lennox prefix) printed a blank brand.
 */
export function manufacturerLabel(e: { manufacturer: string | null; model: string | null }): string {
  return e.manufacturer ? tcase(e.manufacturer) : brandOf(e.model);
}

// Sales credit — who gets sold-by credit on a job. The PRIMARY salesperson stays on
// pegasus_job.sale_tech; these rows are ADDITIONAL credits (2nd salesperson / tech
// lead) with an optional split %. The (held) commission engine reads primary + these;
// the primary implicitly gets the remainder of the split.
export interface JobSalesCredit {
  id: string; job_id: string; empno: string; name: string | null; role: string; split_pct: number | null;
}
export function useJobSalesCredits(jobId: string | null) {
  return useQuery({
    queryKey: ["job_sales_credits", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobSalesCredit[]> => {
      const { data, error } = await supabase
        .from("pegasus_job_sales_credit").select("*").eq("job_id", jobId!).order("created_at").order("id");
      if (error) throw error;
      return (data ?? []) as JobSalesCredit[];
    },
  });
}
export function useAddSalesCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; empno: string; name: string | null; role: string; split_pct: number | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_job_sales_credit").insert({ ...v, created_by: u?.user?.email ?? null });
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["job_sales_credits", v.job_id] }),
  });
}
export function useUpdateSalesCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; job_id: string; role?: string; split_pct?: number | null }) => {
      const patch: Record<string, unknown> = {};
      if (v.role !== undefined) patch.role = v.role;
      if (v.split_pct !== undefined) patch.split_pct = v.split_pct;
      const { error } = await supabase.from("pegasus_job_sales_credit").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["job_sales_credits", v.job_id] }),
  });
}
export function useDeleteSalesCredit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; job_id: string }) => {
      const { error } = await supabase.from("pegasus_job_sales_credit").delete().eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["job_sales_credits", v.job_id] }),
  });
}

/** Persist a new order for the invoice's line-item summary. Overrides upsert by
 *  line_key (a row is created just to hold the sort); custom lines update by id. */
export function useReorderInvoiceLines() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; order: { editId?: string | null; line_key?: string | null; sort: number }[] }) => {
      await Promise.all(v.order.map(async (o) => {
        if (o.editId) {
          const { error } = await supabase.from("pegasus_invoice_line_edit").update({ sort: o.sort, updated_at: new Date().toISOString() }).eq("id", o.editId);
          if (error) throw error;
        } else if (o.line_key) {
          const { error } = await supabase.from("pegasus_invoice_line_edit").insert({ job_id: v.job_id, line_key: o.line_key, sort: o.sort, hidden: false });
          if (error) throw error;
        }
      }));
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["invoice_line_edits", v.job_id] }),
  });
}

/** Edit a one-off custom invoice line (its printed name + amount). */
export function useUpdateCustomInvoiceLine() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; job_id: string; label: string; amount: number | null }) => {
      const label = v.label.trim();
      if (!label) throw new Error("Name the line first");
      const { error } = await supabase.from("pegasus_invoice_line_edit")
        .update({ label, amount: v.amount, updated_at: new Date().toISOString() }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: (_r, v) => qc.invalidateQueries({ queryKey: ["invoice_line_edits", v.job_id] }),
  });
}

export interface SellInstallInput {
  custno: string; locno: string | null; customer_name: string;
  address?: string | null; city?: string | null;
  title: string; sale_tech: string | null; sell_price: number | null;
  source_dispatch?: string | null;
}

/**
 * Sell an install — creates the Job (status "sold") + an UNSCHEDULED install
 * visit on it. The office schedules the visit later (existing schedule flow);
 * close-out (equipment chain / permit / invoice) is the next slice.
 */
export function useSellInstall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: SellInstallInput): Promise<Job> => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const { data: job, error } = await supabase.from("pegasus_job").insert({
        custno: v.custno, locno: v.locno, title: v.title, job_type: "install", status: "sold",
        sale_tech: v.sale_tech, sell_price: v.sell_price, source_dispatch: v.source_dispatch ?? null,
        created_by: email,
      }).select().single();
      if (error) throw error;
      const { error: ve } = await supabase.from("pegasus_dispatch").insert({
        // status/source must satisfy pegasus_dispatch CHECKs: an unscheduled
        // install waiting on the office = 'pending_schedule'; it came from the sale = 'from_dispatch'.
        lane_key: "install", status: "pending_schedule", visit_role: "install", job_id: job.id,
        custno: v.custno, locno: v.locno, customer_name: v.customer_name,
        address: v.address ?? null, city: v.city ?? null,
        summary: v.title || "New system install", source: "from_dispatch", created_by: email,
      });
      if (ve) throw ve;
      return job as Job;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["job_visits"] });
    },
  });
}

/**
 * QUOTE→JOB (Seth 2026-08-18: "shouldn't I have the proposal builder first?"
 * — proposal-first selling). An ACCEPTED proposal is the install job:
 * pegasus_job (status sold, sell_price = the accepted option's total), its
 * units (each remembering the line it was sold as), the unscheduled install
 * visit, the tech's packet, and the quote stamped with job_id.
 *
 * QUOTE-JOB-ONE-01 (2026-09-28): accepting used to make TWO jobs — the
 * database trigger made one and this button made another. Now the database
 * makes it (pegasus_job_from_quote) the moment the proposal is accepted, from
 * the phone, the office or the customer's own link; this finds that job, and
 * makes it the same way only if acceptance couldn't.
 */
export function useCreateJobFromQuote() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { quoteId: string; saleTech?: string | null }): Promise<Job> => {
      const { data: id, error } = await supabase.rpc("pegasus_job_from_quote", { p_quote_id: v.quoteId, p_sale_tech: v.saleTech ?? null });
      if (error) throw error;
      if (!id) throw new Error("Attach a customer to the proposal and pick the option they took first.");
      const { data: job, error: je } = await supabase.from("pegasus_job").select("*").eq("id", id as string).single();
      if (je) throw je;
      return job as Job;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["job_visits"] });
      qc.invalidateQueries({ queryKey: ["job_equipment"] });
      qc.invalidateQueries({ queryKey: ["quote"] });
      qc.invalidateQueries({ queryKey: ["quote_list"] });
    },
  });
}

export interface AddAnotherDayResult {
  jobId: string;
  visitId: string;
}

/**
 * Add another day to a service call (Seth 2026-06-26: "what if it's a multi-day job? I
 * should be able to add on dispatch or invoice"). LOCKED model: PROMOTE the call to a
 * Job (if it isn't one already) so the days share a container + ONE invoice, then create
 * a new UNSCHEDULED visit for the office to schedule. Same job→visits the install flow
 * already uses — not a parallel "linked dispatch". ESC untouched (Pegasus-native only).
 */
export function useAddAnotherDay() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (d: {
      id: string;
      job_id: string | null;
      visit_role: string | null;
      custno: string | null;
      locno: string | null;
      customer_name: string;
      address: string | null;
      city: string | null;
      phone: string | null;
      summary: string;
      service_type_name: string | null;
      lane_key: string;
    }): Promise<AddAnotherDayResult> => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const title = d.service_type_name ?? d.summary ?? "Service call";

      // 1) Promote to a Job the first time a second day is added.
      let jobId = d.job_id;
      if (!jobId) {
        const { data: job, error: je } = await supabase.from("pegasus_job").insert({
          custno: d.custno, locno: d.locno, title, job_type: "service", status: "in_progress",
          source_dispatch: d.id, created_by: email,
        }).select("id").single();
        if (je) throw je;
        jobId = (job as { id: string }).id;
        // Link the original dispatch as visit 1 (keep any existing visit_role).
        const { error: le } = await supabase.from("pegasus_dispatch")
          .update({ job_id: jobId, visit_role: d.visit_role ?? "visit_1", updated_at: new Date().toISOString() })
          .eq("id", d.id);
        if (le) throw le;
      }

      // 2) Create the new day — an unscheduled visit on the job; the office schedules it next.
      const { data: visit, error: ve } = await supabase.from("pegasus_dispatch").insert({
        job_id: jobId, visit_role: "return", lane_key: d.lane_key || "service",
        status: "pending_schedule", source: "from_dispatch",
        custno: d.custno, locno: d.locno, customer_name: d.customer_name,
        address: d.address, city: d.city, phone: d.phone,
        summary: title, service_type_name: d.service_type_name, created_by: email,
      }).select("id").single();
      if (ve) throw ve;

      return { jobId, visitId: (visit as { id: string }).id };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job_visits"] });
      qc.invalidateQueries({ queryKey: ["pending_ticket"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
    },
  });
}

/**
 * Remove an extra day (multi-day "Add another day" undo). Soft-deletes the visit so it
 * stops showing on the job + the board. If removing it leaves a PROMOTED service job with
 * just its lone original call, revert the promotion: unlink the survivor (job_id = null)
 * + delete the now-empty job, so it's a plain standalone dispatch again — exactly as if the
 * day were never added. (Promoted service jobs only; install / new-construction jobs keep
 * their container even at one visit.)
 */
export function useRemoveVisit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { visitId: string; jobId: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const { error } = await supabase.from("pegasus_dispatch")
        .update({ deleted_at: now, deleted_by: email, updated_at: now })
        .eq("id", v.visitId);
      if (error) throw error;
      if (!v.jobId) return;
      const { data: rest } = await supabase.from("pegasus_dispatch")
        .select("id").eq("job_id", v.jobId).is("deleted_at", null);
      const { data: job } = await supabase.from("pegasus_job")
        .select("job_type").eq("id", v.jobId).maybeSingle();
      if (job && (job as { job_type: string }).job_type === "service" && (rest ?? []).length <= 1) {
        // Null job_id on ALL rows of this job (survivor + the just-removed one) so the FK is
        // clear, then delete the empty job. The survivor is a standalone dispatch again.
        await supabase.from("pegasus_dispatch")
          .update({ job_id: null, visit_role: null, updated_at: now })
          .eq("job_id", v.jobId);
        await supabase.from("pegasus_job").delete().eq("id", v.jobId);
      }
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job_visits"] });
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["pending_ticket"] });
      qc.invalidateQueries({ queryKey: ["pending_tickets"] });
      qc.invalidateQueries({ queryKey: ["scheduled_board"] });
    },
  });
}

export interface CreateNCJobInput {
  custno: string; customer_name: string;
  site_address: string | null; site_city: string | null;
  title: string; supervisor: string | null; contract: number | null;
  /** NC-DRAW-02: the builder's PO for this house (prints on every draw). */
  po_number?: string | null;
  /** PERMIT-01: the builder's default for who pulls the permit. */
  permit_by?: PermitBy | null;
}

/**
 * Create a new-construction job under a builder. The home/lot lives at site_address
 * (a Pegasus-native property, not an ESC location — a brand-new lot). Billed to the
 * builder in draws; no single install visit (NC runs for months). job_type drives the
 * NC tab + draws billing.
 */
export function useCreateNCJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: CreateNCJobInput): Promise<Job> => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const { data: job, error } = await supabase.from("pegasus_job").insert({
        custno: v.custno, locno: null, title: v.title, job_type: "new_construction", status: "sold",
        site_address: v.site_address, site_city: v.site_city, supervisor: v.supervisor,
        sell_price: v.contract, created_by: email,
        po_number: (v.po_number ?? "").trim() || null, permit_by: v.permit_by ?? null,
      }).select().single();
      if (error) throw error;
      return job as Job;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); },
  });
}

export function useUpdateJobStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; status: string }) => {
      const patch: Record<string, unknown> = { status: v.status, updated_at: new Date().toISOString() };
      if (v.status === "closed") patch.closed_at = new Date().toISOString();
      const { error } = await supabase.from("pegasus_job").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["job"] });
    },
  });
}

/** Edit a job's core fields (title, sold-by, sell price, stage). */
export function useUpdateJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Pick<Job, "title" | "sale_tech" | "sell_price" | "status" | "source_dispatch" | "invoice_message" | "install_spec" | "install_date" | "is_test">>) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_job").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["jobs"] }); qc.invalidateQueries({ queryKey: ["job"] }); },
  });
}

/** Metro cities for the permit Jurisdiction dropdown — admin-editable list, seeded from
 *  Sedgwick's per-city inspection packets. Type-to-search; a missing city can still be typed. */
export interface Jurisdiction { id: string; name: string; active: boolean }
export function useJurisdictions() {
  return useQuery({
    queryKey: ["jurisdictions"],
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Jurisdiction[]> => {
      const { data } = await supabase.from("pegasus_jurisdiction").select("id, name, active").eq("active", true).order("name");
      return (data ?? []) as Jurisdiction[];
    },
  });
}

export interface CustomerDispatch { dispatch: string; date: string | null; status: string | null }

/** A customer's dispatches (number + date) — to pick the originating service call. */
export function useCustomerDispatches(custno: string | null, locno?: string | null) {
  return useQuery({
    queryKey: ["customer_dispatches", custno, locno ?? null],
    enabled: !!custno,
    staleTime: 60 * 1000,
    queryFn: async (): Promise<CustomerDispatch[]> => {
      let q = supabase.from("v_dispatch_full").select("dispatch, appointment_date, status").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      const { data } = await q.order("appointment_date", { ascending: false, nullsFirst: false }).limit(120);
      const seen = new Set<string>();
      const out: CustomerDispatch[] = [];
      for (const r of (data ?? []) as { dispatch: string; appointment_date: string | null; status: string | null }[]) {
        if (!r.dispatch || seen.has(r.dispatch)) continue;
        seen.add(r.dispatch);
        out.push({ dispatch: r.dispatch, date: r.appointment_date, status: r.status });
      }
      return out.slice(0, 40);
    },
  });
}

/** Delete a Pegasus job + its visits + close-out children (for test cleanup).
 *  JOB-DELETE-01: one server-side call (job_delete RPC) sweeps EVERY child
 *  table, clears the installed-equipment rows, releases any quote pointing
 *  here, and REFUSES when the job still has scheduled/worked visits — those
 *  get cancelled on the Board first, never silently erased. */
export function useDeleteJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("job_delete", { p_id: id });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["installed_equipment"] });
      qc.invalidateQueries({ queryKey: ["quote"] });
      qc.invalidateQueries({ queryKey: ["quotes"] });
      qc.invalidateQueries({ queryKey: ["quote_list"] });
      qc.invalidateQueries({ queryKey: ["quote_job_exists"] });
    },
  });
}

// ── Close-out: equipment chain · permit · close · invoice ───────────────────

export interface JobEquipment {
  id: string; job_id: string; kind: string; category: string | null; manufacturer: string | null; model: string | null; part_number: string | null; serial: string | null;
  qty: number; unit_cost: number | null; unit_price: number | null; pulled_by: string | null; pulled_at: string | null;
  install_confirmed_by: string | null; install_confirmed_at: string | null;
  close_confirmed_at: string | null; notes: string | null;
  /** Storage path of the nameplate photo captured with the serial. */
  serial_photo_path: string | null;
  /** FIELD-CREWS-01: the warehouse's serial when the crew installed a
   *  different unit on site, and why (the office is flagged). */
  serial_before?: string | null;
  serial_change_reason?: string | null;
  /** The model / brand the office sold, kept when the crew installed a
   *  different unit (model_before null = the sold unit went in). */
  model_before?: string | null;
  manufacturer_before?: string | null;
  /** REFRIG-01: the classifier's equipment key (set by a DB trigger — the same
   *  rules the 360 uses), and the refrigerant facts read off the data tag. */
  equip_key?: string | null;
  /** The pricebook item it was added from (efficiency, specs). */
  item_id?: string | null;
  refrigerant?: string | null;
  factory_charge_oz?: number | null;
  factory_lineset_ft?: number | null;
  /** JOB-SOLD-01: the proposal line this unit was sold as (null = added on the job). */
  quote_line_id?: string | null;
  /** QUOTE-POLISH-08: this unit's warranty — from the proposal line, else the
   *  pricebook model, else the series rule; an evaporator coil takes the
   *  condenser's (DB triggers). Posted onto the customer's equipment at close. */
  warranty_parts_years?: number | null;
  warranty_labor_years?: number | null;
}
export interface JobPermit {
  id: string; job_id: string; permit_no: string | null; jurisdiction: string | null;
  trade: string | null; status: PermitStatus; fee: number | null; pulled_by: string | null; pulled_at: string | null; notes: string | null;
  /** PERMIT-01: who asked for it (a crew email or an office login), when, and why. */
  requested_by: string | null; requested_at: string | null; request_note: string | null;
  issued_at: string | null; finaled_at: string | null;
  /** The builder's own permit, recorded for reference — never billed, never "ours". */
  by_builder: boolean;
  source: string | null;
  created_at: string;
}

/* ── PERMIT-01 (Seth 2026-09-24) ─────────────────────────────────────────
 * Who pulls the permit is a JOB answer: we do (installs default), the
 * builder does ("by others" — common on new construction), or none needed.
 * A permit moves Requested → Applied → Issued → Finaled (or Cancelled);
 * only Applied-or-later counts as ON FILE. Competitor frame: ServiceTitan
 * shops fake this with tags + a coordinator list; Buildertrend-style
 * builders pull their own. We keep one real record and one queue. */
export type PermitBy = "sedgwick" | "builder" | "none";
export type PermitStatus = "requested" | "applied" | "issued" | "finaled" | "cancelled";
export const PERMIT_STATUSES: PermitStatus[] = ["requested", "applied", "issued", "finaled", "cancelled"];
export const PERMIT_STATUS_LABEL: Record<PermitStatus, string> = {
  requested: "Requested", applied: "Applied", issued: "Issued", finaled: "Finaled", cancelled: "Cancelled",
};
export const PERMIT_BY_LABEL: Record<PermitBy, string> = {
  sedgwick: "We pull it", builder: "Builder pulls it", none: "Not needed",
};
/** One of OUR permits, pulled (Applied or later) — what close-out and the queue count. */
export function permitIsOnFile(p: { status: string; by_builder?: boolean | null }): boolean {
  return !p.by_builder && (p.status === "applied" || p.status === "issued" || p.status === "finaled");
}
export function isNCJob(j: { job_type?: string | null } | null | undefined): boolean {
  return j?.job_type === "new_construction" || j?.job_type === "nc";
}
/** The answer that governs the job: an explicit builder / none wins; the
 *  legacy post-install "No Permit Needed" switch reads as none; an undecided
 *  install assumes we pull it; an undecided new-construction house is null
 *  (the Permits tab asks). */
export function effectivePermitBy(
  job: { permit_by?: PermitBy | null; job_type?: string | null } | null | undefined,
  postInstall?: { permit_required: boolean } | null,
): PermitBy | null {
  if (job?.permit_by === "builder" || job?.permit_by === "none") return job.permit_by;
  if (postInstall && postInstall.permit_required === false) return "none";
  if (job?.permit_by === "sedgwick") return "sedgwick";
  return isNCJob(job) ? null : "sedgwick";
}

export interface PermitInspection {
  id: string; permit_id: string; job_id: string | null;
  kind: "rough_in" | "final" | "other"; label: string | null;
  scheduled_date: string | null;
  result: "needed" | "scheduled" | "passed" | "failed" | "cancelled";
  inspector: string | null; notes: string | null;
  created_by: string | null; created_at: string;
}
export const INSPECTION_KIND_LABEL: Record<PermitInspection["kind"], string> = { rough_in: "Rough-In", final: "Final", other: "Other" };
export const INSPECTION_RESULT_LABEL: Record<PermitInspection["result"], string> = {
  needed: "Needs Scheduling", scheduled: "Scheduled", passed: "Passed", failed: "Failed", cancelled: "Cancelled",
};
export interface JobInvoiceLine { id: string; kind: string | null; description: string | null; amount: number | null; sort: number }
export interface JobInvoice { id: string; job_id: string; status: string; label: string | null; total: number | null; created_at: string; lines: JobInvoiceLine[] }

export function useJobEquipment(jobId: string | null) {
  return useQuery({
    queryKey: ["job_equipment", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobEquipment[]> => {
      // `id` tiebreak is REQUIRED: units added in one batch share a created_at, and an
      // UPDATE (pull / confirm / edit) rewrites the row, flipping an unordered tie — the
      // row visibly jumps position. Any list ordered by a non-unique column needs this.
      const { data } = await supabase.from("pegasus_job_equipment").select("*").eq("job_id", jobId!).order("created_at").order("id");
      return (data ?? []) as JobEquipment[];
    },
  });
}

export function useAddJobEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; kind?: string; category: string | null; manufacturer?: string | null; model: string | null; part_number?: string | null; qty?: number; unit_cost?: number | null; unit_price?: number | null; item_id?: string | null }) => {
      const { error } = await supabase.from("pegasus_job_equipment").insert({
        job_id: v.job_id, kind: v.kind ?? "equipment", category: v.category, manufacturer: v.manufacturer ?? null, model: v.model, part_number: v.part_number ?? null, qty: v.qty ?? 1, unit_cost: v.unit_cost ?? null, unit_price: v.unit_price ?? null, item_id: v.item_id ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_equipment"] }),
  });
}

/** Edit an existing unit's office data — category, model, qty, our cost, customer price. */
export function useUpdateJobEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Pick<JobEquipment, "category" | "manufacturer" | "model" | "part_number" | "qty" | "unit_cost" | "unit_price" | "kind" | "item_id">>) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_job_equipment").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_equipment"] }),
  });
}

/** SERIAL-GUARD-01 (Seth 2026-10-15): where else does this serial live?
 *  `jobs` = other job units carrying it (open ones are the double allocation
 *  the database refuses); `installed` = units already in a customer's home
 *  (Pegasus-closed or the ESC mirror) — a typo or a returned unit, worth a look. */
export interface SerialConflicts {
  jobs: Array<{ equip_id: string; job_id: string; job_title: string | null; job_status: string | null; dispatch: string | null; customer: string | null; install_date: string | null; kind: string | null; manufacturer: string | null; model: string | null; pulled_by: string | null; pulled_at: string | null; open: boolean }>;
  installed: Array<{ source: "pegasus" | "esc"; custno: string | null; customer: string | null; address: string | null; city: string | null; manufacturer: string | null; model: string | null; install_date: string | null; job_id: string | null }>;
}
export async function fetchSerialConflicts(serial: string, excludeEquipId: string | null): Promise<SerialConflicts> {
  const { data, error } = await supabase.rpc("pegasus_serial_conflicts", { p_serial: serial, p_exclude_id: excludeEquipId });
  if (error) throw error;
  const d = (data ?? {}) as Partial<SerialConflicts>;
  return { jobs: d.jobs ?? [], installed: d.installed ?? [] };
}

/** SERIAL-GUARD-01: the emergency reassignment — the unit the warehouse pulled
 *  for job A goes out on job B. One transaction: A's unit goes back to
 *  needs-a-pull (serial, photo, pulled state cleared), B gets the serial, and
 *  both jobs get a note saying who moved what. */
export function useMoveSerial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { fromEquipId: string; toEquipId: string }) => {
      const { error } = await supabase.rpc("pegasus_move_serial", { p_from_equip_id: v.fromEquipId, p_to_equip_id: v.toEquipId });
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["job_equipment"] });
      qc.invalidateQueries({ queryKey: ["internal_notes"] });
    },
  });
}

/** The database's refusal, in the warehouse's words. */
export const serialErrorText = (e: unknown): string => {
  const m = (e as { message?: string } | null)?.message ?? String(e);
  return m.replace(/^.*SERIAL_IN_USE:\s*/i, "").replace(/\s*—\s*move it to this job or clear it there first\s*$/i, "");
};

/** Warehouse logs the serial (step 1 of the 3-step chain). */
export function useSetEquipmentSerial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; serial: string | null; serial_photo_path?: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const has = !!(v.serial && v.serial.trim());
      const patch: Record<string, unknown> = { serial: v.serial?.trim() || null };
      // A captured serial implies the unit is pulled, but we never UN-pull on clear —
      // the explicit "Pulled" toggle owns that (accessories often have no serial).
      if (has) { patch.pulled_by = email; patch.pulled_at = new Date().toISOString(); }
      if (v.serial_photo_path !== undefined) patch.serial_photo_path = v.serial_photo_path;
      const { error } = await supabase.from("pegasus_job_equipment").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_equipment"] }),
  });
}

/** Installer confirms the unit before installing (step 2 — office-stub until mobile). */
export function useConfirmInstallEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; confirmed: boolean }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const { error } = await supabase.from("pegasus_job_equipment").update({
        install_confirmed_by: v.confirmed ? email : null,
        install_confirmed_at: v.confirmed ? new Date().toISOString() : null,
      }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_equipment"] }),
  });
}

/** Warehouse "Pulled" toggle — independent of serial (accessories may not have one). */
export function useSetEquipmentPulled() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; pulled: boolean }) => {
      const { data: u } = await supabase.auth.getUser();
      const email = u?.user?.email ?? null;
      const { error } = await supabase.from("pegasus_job_equipment").update({
        pulled_by: v.pulled ? email : null,
        pulled_at: v.pulled ? new Date().toISOString() : null,
      }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_equipment"] }),
  });
}

export function useDeleteJobEquipment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await supabase.from("pegasus_job_equipment").delete().eq("id", id); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_equipment"] }),
  });
}

export function useJobPermits(jobId: string | null) {
  return useQuery({
    queryKey: ["job_permits", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobPermit[]> => {
      const { data } = await supabase.from("pegasus_permit").select("*").eq("job_id", jobId!).order("created_at").order("id");
      return (data ?? []) as JobPermit[];
    },
  });
}

/** Every permit query/queue refreshes together (the queryKey-mismatch lesson). */
function invalidatePermits(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ["job_permits"] });
  qc.invalidateQueries({ queryKey: ["permit_inspections"] });
  qc.invalidateQueries({ queryKey: ["job_queues"] });
}

export function useAddPermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; permit_no: string | null; jurisdiction: string | null; trade?: string | null; status?: PermitStatus; fee?: number | null; by_builder?: boolean; notes?: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const who = u?.user?.email ?? null;
      const now = new Date().toISOString();
      const status: PermitStatus = v.status ?? "applied";
      const { error } = await supabase.from("pegasus_permit").insert({
        job_id: v.job_id, permit_no: v.permit_no, jurisdiction: v.jurisdiction, trade: v.trade ?? null, fee: v.fee ?? null,
        status, by_builder: v.by_builder ?? false, notes: v.notes ?? null,
        // Requested = nobody pulled it yet; anything later was pulled by whoever attached it.
        pulled_by: status === "requested" || v.by_builder ? null : who,
        pulled_at: status === "requested" || v.by_builder ? null : now,
        requested_by: status === "requested" ? who : null,
        requested_at: status === "requested" ? now : null,
        issued_at: status === "issued" || status === "finaled" ? now : null,
        finaled_at: status === "finaled" ? now : null,
        source: v.by_builder ? "builder" : "office",
      });
      if (error) throw error;
    },
    onSuccess: () => invalidatePermits(qc),
  });
}

/** PERMIT-01 — "Request permit" from the phone (or the office): a Requested
 *  permit lands on the job and puts it at the top of the permit desk's queue.
 *  The note says why ("inspector Thursday", "super asked"). */
export function useRequestPermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; trade?: string | null; note?: string | null; requestedBy?: string | null; jurisdiction?: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_permit").insert({
        job_id: v.job_id, trade: v.trade ?? "Mechanical", status: "requested", jurisdiction: v.jurisdiction ?? null,
        requested_by: v.requestedBy ?? u?.user?.email ?? null, requested_at: new Date().toISOString(),
        request_note: (v.note ?? "").trim() || null, source: "field",
      });
      if (error) throw error;
    },
    onSuccess: () => invalidatePermits(qc),
  });
}

export function useDeletePermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_permit").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidatePermits(qc),
  });
}

/** Edit an attached permit — the permit # and fee often arrive days after the
 *  permit is pulled, so the office fills them in later. A status move stamps
 *  its moment (issued / finaled) and who pulled it (Requested → Applied). */
export function useUpdatePermit() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; permit_no?: string | null; fee?: number | null; jurisdiction?: string | null; trade?: string | null; status?: PermitStatus; notes?: string | null; prevStatus?: PermitStatus }) => {
      const { id, prevStatus, ...patch } = v;
      const row: Record<string, unknown> = { ...patch, updated_at: new Date().toISOString() };
      if (v.status && v.status !== prevStatus) {
        const now = new Date().toISOString();
        const who = (await supabase.auth.getUser()).data.user?.email ?? null;
        if (prevStatus === "requested" && v.status !== "cancelled") { row.pulled_by = who; row.pulled_at = now; }
        if ((v.status === "issued" || v.status === "finaled") && (prevStatus === "requested" || prevStatus === "applied")) row.issued_at = now;
        if (v.status === "finaled") row.finaled_at = now;
        if (prevStatus === "finaled" && v.status !== "finaled") row.finaled_at = null;
      }
      const { error } = await supabase.from("pegasus_permit").update(row).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidatePermits(qc),
  });
}

/** PERMIT-01 — set who pulls the permit. Mirrors the post-install tracker's
 *  switch so "permit info" stops (or resumes) going to the customer, and the
 *  legacy "No Permit Needed" can never contradict the job. */
export function useSetPermitBy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job: Pick<Job, "id" | "custno" | "locno" | "source_dispatch">; permitBy: PermitBy }) => {
      const { error } = await supabase.from("pegasus_job").update({ permit_by: v.permitBy, updated_at: new Date().toISOString() }).eq("id", v.job.id);
      if (error) throw error;
      const who = (await supabase.auth.getUser()).data.user?.email ?? "office";
      const key = v.job.source_dispatch ? "dispatch" : "job_id";
      const { error: piErr } = await supabase.from("pegasus_post_install").upsert({
        dispatch: v.job.source_dispatch ?? null, job_id: v.job.id,
        custno: v.job.custno ?? null, locno: v.job.locno ?? null, created_by: who,
        updated_at: new Date().toISOString(),
        permit_required: v.permitBy === "sedgwick",
      }, { onConflict: key });
      if (piErr) throw piErr;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["job"] });
      qc.invalidateQueries({ queryKey: ["post_install"] });
      qc.invalidateQueries({ queryKey: ["job_queues"] });
    },
  });
}

export function usePermitInspections(jobId: string | null) {
  return useQuery({
    queryKey: ["permit_inspections", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<PermitInspection[]> => {
      const { data, error } = await supabase.from("pegasus_permit_inspection").select("*").eq("job_id", jobId!)
        .order("scheduled_date", { ascending: true, nullsFirst: false }).order("created_at").order("id");
      if (error) throw error;
      return (data ?? []) as PermitInspection[];
    },
  });
}

export function useAddInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { permit_id: string; job_id: string; kind: PermitInspection["kind"]; scheduled_date: string | null; result: PermitInspection["result"]; notes?: string | null }) => {
      const who = (await supabase.auth.getUser()).data.user?.email ?? null;
      const { error } = await supabase.from("pegasus_permit_inspection").insert({ ...v, notes: v.notes ?? null, created_by: who });
      if (error) throw error;
      await finalIfPassed(v.permit_id, v.kind, v.result);
    },
    onSuccess: () => invalidatePermits(qc),
  });
}

export function useUpdateInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; permit_id: string; kind: PermitInspection["kind"]; scheduled_date?: string | null; result?: PermitInspection["result"]; notes?: string | null }) => {
      const { id, permit_id, kind, ...patch } = v;
      const { error } = await supabase.from("pegasus_permit_inspection").update({ ...patch, updated_at: new Date().toISOString() }).eq("id", id);
      if (error) throw error;
      if (patch.result) await finalIfPassed(permit_id, kind, patch.result);
    },
    onSuccess: () => invalidatePermits(qc),
  });
}

export function useDeleteInspection() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from("pegasus_permit_inspection").delete().eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => invalidatePermits(qc),
  });
}

/** A passed FINAL inspection finals the permit (the city closed it out). */
async function finalIfPassed(permitId: string, kind: PermitInspection["kind"], result: PermitInspection["result"]) {
  if (kind !== "final" || result !== "passed") return;
  const now = new Date().toISOString();
  await supabase.from("pegasus_permit").update({ status: "finaled", finaled_at: now, updated_at: now })
    .eq("id", permitId).neq("status", "finaled").neq("status", "cancelled");
  // A finaled permit was issued — stamp it if the office skipped that step.
  await supabase.from("pegasus_permit").update({ issued_at: now }).eq("id", permitId).is("issued_at", null).eq("status", "finaled");
}

/** Gated close-out — calls the close_job RPC (raises if no serial / no permit). */
export function useCloseJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (jobId: string): Promise<string> => {
      const { data, error } = await supabase.rpc("close_job", { p_job_id: jobId });
      if (error) throw error;
      return data as string;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["jobs"] });
      qc.invalidateQueries({ queryKey: ["job"] });
      qc.invalidateQueries({ queryKey: ["job_equipment"] });
      qc.invalidateQueries({ queryKey: ["job_invoice"] });
      qc.invalidateQueries({ queryKey: ["installed_equipment"] });
      // FIELD-PAY-01: close moves the deposit + field payments onto the final
      // invoice and voids the deposit invoice — every money view re-reads.
      qc.invalidateQueries({ queryKey: ["job_invoices"] });
      qc.invalidateQueries({ queryKey: ["job_money"] });
      qc.invalidateQueries({ queryKey: ["job_payments"] });
      qc.invalidateQueries({ queryKey: ["payments"] });
      qc.invalidateQueries({ queryKey: ["invoice_doc"] });
      qc.invalidateQueries({ queryKey: ["pegasus_customer_invoices"] });
      qc.invalidateQueries({ queryKey: ["invoices_unified_peg"] });
    },
  });
}

export function useJobInvoice(jobId: string | null) {
  return useQuery({
    queryKey: ["job_invoice", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobInvoice | null> => {
      const { data: inv } = await supabase.from("pegasus_job_invoice").select("*").eq("job_id", jobId!)
        .order("created_at", { ascending: false }).limit(1).maybeSingle();
      if (!inv) return null;
      const { data: lines } = await supabase.from("pegasus_job_invoice_line").select("*").eq("invoice_id", (inv as JobInvoice).id).order("sort");
      return { ...(inv as JobInvoice), lines: (lines ?? []) as JobInvoiceLine[] };
    },
  });
}

// A finalized invoice document (install OR service) read by its own id — the unified
// invoice the portal / Stripe / reports / customer history all render from.
export interface InvoiceDocLine { id: string; description: string | null; qty: number | null; unit_price: number | null; amount: number | null; kind: string | null; sort: number; print: boolean; waived: boolean | null }
export interface InvoiceDoc {
  id: string; type: string; dispatch_no: string | null; custno: string | null; locno: string | null;
  job_name: string | null; comfort_advisor: string | null; technician: string | null; completed_date: string | null;
  tech_notes: string | null; paid_method: string | null; paid_reference: string | null; paid_amount: number | null;
  signature_url: string | null; invoice_no: string | null; total: number | null; created_at: string;
  po_number: string | null;
  job_id: string | null;
  /** NC-DRAW-02: the draw's stage (Rough-In, Final…) / a deposit's label. */
  label?: string | null;
  lines: InvoiceDocLine[];
}
export function useInvoiceDoc(invoiceId: string | null) {
  return useQuery({
    queryKey: ["invoice_doc", invoiceId],
    enabled: !!invoiceId,
    queryFn: async (): Promise<InvoiceDoc | null> => {
      const { data: inv } = await supabase.from("pegasus_job_invoice").select("*").eq("id", invoiceId!).maybeSingle();
      if (!inv) return null;
      const { data: lines } = await supabase.from("pegasus_job_invoice_line").select("*").eq("invoice_id", invoiceId!).order("sort");
      return { ...(inv as InvoiceDoc), lines: (lines ?? []) as InvoiceDocLine[] };
    },
  });
}

/** All invoices on a job (draws + the close-out invoice) — for the billing view. */
export function useJobInvoices(jobId: string | null) {
  return useQuery({
    queryKey: ["job_invoices", jobId], enabled: !!jobId,
    queryFn: async (): Promise<JobInvoice[]> => {
      const { data } = await supabase.from("pegasus_job_invoice").select("*").eq("job_id", jobId!).order("created_at").order("id");
      return ((data ?? []) as JobInvoice[]).map((i) => ({ ...i, lines: [] }));
    },
  });
}

/** Installed equipment on the customer file (Pegasus-native) — for the 360. */
export interface InstalledEquipment {
  id: string; category: string | null; brand: string | null; model: string | null; serial: string | null;
  install_date: string | null; job_id: string | null;
  /** EQUIP-ROOM-01: the room/area the unit serves — techs + office both set it at entry. */
  equip_loc?: string | null;
  /** REFRIG-01: posted at close — the refrigerant, the total charge in the system and the line set. */
  refrigerant?: string | null; charge_oz?: number | null; lineset_ft?: number | null;
  /** QUOTE-POLISH-08: the warranty this unit was sold with — carried from the
   *  proposal line through the job and posted here when the install closed. */
  warranty_parts_years?: number | null; warranty_labor_years?: number | null;
}
/** A customer's jobs — for attaching a PO to the right one (PO→job). */
export function useCustomerJobs(custno: string | null) {
  return useQuery({
    queryKey: ["customer_jobs", custno],
    enabled: !!custno,
    staleTime: 15 * 1000,
    queryFn: async (): Promise<Job[]> => {
      const { data } = await supabase.from("pegasus_job").select("*").eq("custno", custno!).order("created_at", { ascending: false });
      return (data ?? []) as Job[];
    },
  });
}

export interface JobPart {
  po_id: string; po_number: string | null; part_number: string | null; description: string | null;
  qty: number; unit_cost: number; received: boolean;
}
/** Parts ordered for a job — from POs attached to it (job-costing material list). */
export function useJobParts(jobId: string | null) {
  return useQuery({
    queryKey: ["job_parts", jobId],
    enabled: !!jobId,
    queryFn: async (): Promise<JobPart[]> => {
      const { data: pos } = await supabase.from("pegasus_po").select("id,po_number,status").eq("job_id", jobId!);
      const poList = (pos ?? []) as { id: string; po_number: string | null; status: string }[];
      if (poList.length === 0) return [];
      const meta = new Map(poList.map((p) => [p.id, p]));
      const { data: lines } = await supabase.from("pegasus_po_line").select("po_id,part_number,description,qty,unit_cost").in("po_id", poList.map((p) => p.id));
      return ((lines ?? []) as { po_id: string; part_number: string | null; description: string | null; qty: number; unit_cost: number }[]).map((l) => ({
        po_id: l.po_id, po_number: meta.get(l.po_id)?.po_number ?? null,
        part_number: l.part_number, description: l.description,
        qty: Number(l.qty || 0), unit_cost: Number(l.unit_cost || 0),
        received: meta.get(l.po_id)?.status === "received",
      }));
    },
  });
}

export function useInstalledEquipment(custno: string | null, locno?: string | null) {
  return useQuery({
    queryKey: ["installed_equipment", custno, locno ?? null],
    enabled: !!custno,
    queryFn: async (): Promise<InstalledEquipment[]> => {
      let q = supabase.from("pegasus_equipment").select("id,category,brand,model,serial,install_date,job_id,equip_loc,refrigerant,charge_oz,lineset_ft,warranty_parts_years,warranty_labor_years").eq("custno", custno!);
      if (locno) q = q.eq("locno", locno);
      const { data } = await q.order("install_date", { ascending: false, nullsFirst: false });
      return (data ?? []) as InstalledEquipment[];
    },
  });
}

/** ROOM-EDIT-01: rename/move a Pegasus-native unit's room. ESC-mirror units
 *  do the same through pegasus_equipment_override (ESC untouched, DEC-22). */
export function useSetUnitRoom() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; custno: string; equip_loc: string | null }) => {
      const { error } = await supabase.from("pegasus_equipment").update({ equip_loc: v.equip_loc }).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["installed_equipment"] });
    },
  });
}

// ── New-construction costing: daily labor + change orders ───────────────────

export interface JobLabor {
  id: string; job_id: string; crew: string | null; tech_empno: string | null; work_date: string | null;
  hours: number; rate: number | null; clock_in: string | null; clock_out: string | null;
  source: string; approved: boolean; notes: string | null;
}
export function useJobLabor(jobId: string | null) {
  return useQuery({
    queryKey: ["job_labor", jobId], enabled: !!jobId,
    queryFn: async (): Promise<JobLabor[]> => {
      const { data } = await supabase.from("pegasus_job_labor").select("*").eq("job_id", jobId!).order("work_date", { ascending: false, nullsFirst: false });
      return (data ?? []) as JobLabor[];
    },
  });
}
export function useAddJobLabor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; crew: string | null; tech_empno?: string | null; work_date: string | null; hours: number; rate: number | null; source?: string }) => {
      const { data: u } = await supabase.auth.getUser();
      // source defaults to 'office' (paper keyed by the office today). The mobile
      // crew app will write the same shape with source='mobile' + clock_in/out.
      const { error } = await supabase.from("pegasus_job_labor").insert({ ...v, source: v.source ?? "office", created_by: u?.user?.email ?? null });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_labor"] }),
  });
}
export function useDeleteJobLabor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await supabase.from("pegasus_job_labor").delete().eq("id", id); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_labor"] }),
  });
}

// ── Job materials (installer-logged; print-aware for the customer invoice) ────
// Materials/accessories used on the job — incl. inventory-only items like refrigerant.
// `qty` is the standard add, `qty_actual` is what the installer used in the field;
// `print` controls whether the line shows on the customer invoice (ESC's Print box).
export interface JobMaterial {
  id: string; job_id: string; description: string; kind: string;
  qty: number; qty_actual: number | null; unit: string | null;
  unit_cost: number | null; print: boolean; sort: number;
  /** FIELD-CREWS-01: why "used" differs from planned, who changed it, when;
   *  source = office (planned) · field (the crew needed it) · addon (the
   *  material behind an add-on sold on site). */
  change_reason?: string | null; changed_by?: string | null; changed_at?: string | null;
  source?: "office" | "field" | "addon" | "request"; item_id?: string | null;
  /** MATERIAL-REQ-01: a crew's request line — where it is (requested → being
   *  pulled → ready on a shelf / on order → picked up), the PO it's on (a
   *  Pegasus PO or an ESC PO #), when it's due, and the crew's own words. */
  request_id?: string | null;
  pull_status?: PullStatus | null;
  shelf?: string | null; po_id?: string | null; esc_po?: string | null;
  due_date?: string | null; need_by?: string | null; typed?: string | null;
  status_at?: string | null; status_by?: string | null;
}
export type PullStatus = "requested" | "pulling" | "ready" | "ordered" | "picked_up" | "cancelled";
/** Cost of a material = actual-used (falls back to standard qty) × unit cost. */
export function materialCost(m: JobMaterial): number {
  const q = m.qty_actual != null ? Number(m.qty_actual) : Number(m.qty || 0);
  return q * Number(m.unit_cost || 0);
}
export function useJobMaterials(jobId: string | null) {
  return useQuery({
    queryKey: ["job_materials", jobId], enabled: !!jobId,
    queryFn: async (): Promise<JobMaterial[]> => {
      const { data } = await supabase.from("pegasus_job_material").select("*").eq("job_id", jobId!).order("sort").order("created_at").order("id");
      return (data ?? []) as JobMaterial[];
    },
  });
}
export function useAddJobMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; description: string; kind?: string; qty?: number; unit?: string | null; unit_cost?: number | null; print?: boolean; sort?: number; item_id?: string | null }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_job_material").insert({
        job_id: v.job_id, description: v.description, kind: v.kind ?? "material",
        qty: v.qty ?? 1, unit: v.unit ?? null, unit_cost: v.unit_cost ?? null,
        print: v.print ?? true, sort: v.sort ?? 0, item_id: v.item_id ?? null, created_by: u?.user?.email ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_materials"] }),
  });
}
export function useUpdateJobMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string } & Partial<Pick<JobMaterial, "description" | "qty" | "qty_actual" | "unit" | "unit_cost" | "print" | "kind" | "sort">>) => {
      const { id, ...patch } = v;
      const { error } = await supabase.from("pegasus_job_material").update(patch).eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_materials"] }),
  });
}
export function useDeleteJobMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await supabase.from("pegasus_job_material").delete().eq("id", id); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["job_materials"] }),
  });
}

export interface ChangeOrder {
  id: string; job_id: string; description: string | null; amount: number; status: string;
  approved_by: string | null; approved_at: string | null;
  /** FIELD-CREWS-01: an add-on sold on site — the customer signed on the
   *  iPad (signature_url = PNG data URL), credited to sold_by for commission. */
  source?: "office" | "field"; item_id?: string | null; qty?: number | null; unit_price?: number | null;
  category?: string | null; sold_by_empno?: string | null; sold_by_name?: string | null;
  dispatch_id?: string | null; signature_url?: string | null; signed_name?: string | null; signed_at?: string | null;
  created_by?: string | null; created_at?: string;
}
export function useChangeOrders(jobId: string | null) {
  return useQuery({
    queryKey: ["change_orders", jobId], enabled: !!jobId,
    queryFn: async (): Promise<ChangeOrder[]> => {
      const { data } = await supabase.from("pegasus_change_order").select("*").eq("job_id", jobId!).order("created_at").order("id");
      return (data ?? []) as ChangeOrder[];
    },
  });
}
export function useAddChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { job_id: string; description: string | null; amount: number }) => {
      const { data: u } = await supabase.auth.getUser();
      const { error } = await supabase.from("pegasus_change_order").insert({ ...v, status: "pending", created_by: u?.user?.email ?? null });
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["change_orders"] }),
  });
}
export function useUpdateChangeOrderStatus() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (v: { id: string; status: string }) => {
      const { data: u } = await supabase.auth.getUser();
      const patch: Record<string, unknown> = { status: v.status };
      if (v.status === "approved") { patch.approved_by = u?.user?.email ?? null; patch.approved_at = new Date().toISOString(); }
      const { error } = await supabase.from("pegasus_change_order").update(patch).eq("id", v.id);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["change_orders"] }),
  });
}
export function useDeleteChangeOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => { await supabase.from("pegasus_change_order").delete().eq("id", id); },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["change_orders"] }),
  });
}
